import { DataTable } from '@/components/features/shared/DataTable'
import { PriceLabelSimple } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { formatPaymentPlanStatus, getPaymentPlanStatusColor, PaymentPlanData } from '@/domain/agreements/agreement'
import { ServiceType } from '@/domain/offerings/offering'
import { useOfferings } from '@/hooks/api/useOfferings'
import { usePaymentPlans } from '@/hooks/api/usePaymentPlans'
import { useServerPagination } from '@/hooks/useServerPagination'
import { Paths } from '@/routes/paths'
import { formatDocNumber } from '@/utils/helpers'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { addMonths, endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { es } from 'date-fns/locale'
import { ArrowLeft, ArrowRight, CreditCard, FileText, Loader2, Search } from 'lucide-react'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader'

export const PaymentPlansManagement = () => {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as any

  const statusFilter = searchParams.status || 'all'
  const serviceTypeFilter = searchParams.serviceType || 'all'
  const offeringFilter = searchParams.offeringId || 'all'
  const monthParam = searchParams.month // YYYY-MM
  const showByMonth = !!monthParam

  const selectedMonth = monthParam ? new Date(monthParam + '-01T12:00:00') : new Date()

  const {
    pagination,
    setPagination: handlePaginationChange,
    search,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    limit,
  } = useServerPagination({
    clearDefaults: {
      status: 'all',
      serviceType: 'all',
      offeringId: 'all',
    },
    resetOn: [statusFilter, serviceTypeFilter, offeringFilter],
  })

  const { data: offerings } = useOfferings({ serviceType: serviceTypeFilter === 'all' ? undefined : serviceTypeFilter as ServiceType })

  const { data: paymentPlans, isLoading } = usePaymentPlans({
    search,
    status: statusFilter === 'all' ? undefined : statusFilter,
    serviceType: serviceTypeFilter === 'all' ? undefined : serviceTypeFilter,
    offeringId: offeringFilter === 'all' ? undefined : offeringFilter,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    dateFrom: showByMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
    dateTo: showByMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : undefined,
  })

  // console.log(paymentPlans)

  const handlePrevMonth = () => {
    const newMonth = subMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM') })
  }

  const handleNextMonth = () => {
    const newMonth = addMonths(selectedMonth, 1)
    updateQueryParams({ month: format(newMonth, 'yyyy-MM') })
  }

  const handleCurrentMonth = () => {
    updateQueryParams({ month: format(new Date(), 'yyyy-MM') })
  }

  const handleSelect = (plan: PaymentPlanData) => {
    navigate({
      to: `${Paths.Management.Contracts}/${plan.id}`,
    })
  }

  const columns: ColumnDef<PaymentPlanData>[] = [
    {
      accessorKey: 'contract',
      header: 'Contrato',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium">{plan.contractNumber}</span>
            <span className="text-xs text-muted-foreground">{plan.offeringName}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'client',
      header: 'Cliente',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className="font-medium flex flex-col">
            <span className="text-md font-medium">{plan.userName}</span>
            <span className="text-xs text-muted-foreground">{plan.userDocType} {formatDocNumber(plan.userDocNumber, plan.userDocType)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'initialPeriodDate',
      header: 'Período inicial',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className="text-sm">
            {format(new Date(plan.initialPeriodDate), 'dd/MM/yyyy')}
          </div>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <Badge className={getPaymentPlanStatusColor(plan.status)}>
            {formatPaymentPlanStatus(plan.status)}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'initialAmount',
      header: 'Entrega inicial',
      cell: ({ row }) => {
        const plan = row.original
        return <PriceLabelSimple price={plan.initialAmount} />
      },
    },
    {
      accessorKey: 'installmentInfo',
      header: 'Financiación',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className="flex flex-col">
            <PriceLabelSimple price={plan.installmentAmount} />
            <div className="text-muted-foreground text-xs flex items-center gap-1">
              <PriceLabelSimple price={plan.installmentValue} /> / {plan.installments}
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'totalAmount',
      header: 'Monto Total',
      cell: ({ row }) => {
        const plan = row.original
        return <PriceLabelSimple price={plan.totalAmount} />
      },
    },
    {
      accessorKey: 'totalPaidAmount',
      header: 'Pagado',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className={`font-bold ${plan.totalPaidAmount > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <PriceLabelSimple price={plan.totalPaidAmount} />
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <CreditCard className="h-4 w-4" />
              {plan.installmentsPaid} / {plan.installments}
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'totalOutstandingAmount',
      header: 'Deuda',
      cell: ({ row }) => {
        const plan = row.original
        return (
          <div className={`font-bold w-[max-content] ${plan.totalOutstandingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
            <PriceLabelSimple price={plan.totalOutstandingAmount} />
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-3xl font-bold tracking-tight">Planes de Pago</h1>
        <p className="text-muted-foreground">
          Gestión de contrataciones con planes de cuotas
        </p>
      </NavHeader>

      <div className="flex flex-col gap-4">
        {/* Month Navigation */}
        <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg border">
          <div className="flex items-center space-x-2">
            <Switch
              id="show-by-month"
              checked={showByMonth}
              onCheckedChange={(checked) => {
                updateQueryParams({
                  month: checked ? (monthParam || format(new Date(), 'yyyy-MM')) : undefined,
                  page: 1
                })
              }}
            />
            <Label htmlFor="show-by-month">Ver planes por mes de creación</Label>
          </div>

          {showByMonth && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevMonth}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[150px] text-center font-medium capitalize">
                {format(selectedMonth, 'MMMM yyyy', { locale: es })}
              </div>
              <Button variant="outline" size="sm" onClick={handleNextMonth}>
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCurrentMonth} className="ml-2">
                Mes actual
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-end gap-4">
        <div className="flex flex-col gap-y-2 w-full max-w-sm">
          <Label>Búsqueda</Label>
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente o contrato..."
              className="pl-8"
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-2">
          <Label>Estado</Label>
          <Select value={statusFilter} onValueChange={(val) => {
            updateQueryParams({ status: val })
          }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="created">Creado</SelectItem>
              <SelectItem value="active">Activo</SelectItem>
              <SelectItem value="payed">Pagado</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-y-2">
          <Label>Tipo de Servicio</Label>
          <Select value={serviceTypeFilter} onValueChange={(val) => {
            updateQueryParams({ serviceType: val, offeringId: 'all', page: 1 })
          }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Tipo de servicio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value={ServiceType.ABONOS}>Abonos</SelectItem>
              <SelectItem value={ServiceType.PARCELAS}>Parcelas</SelectItem>
              <SelectItem value={ServiceType.CREMACIONES}>Cremaciones</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-y-2">
          <Label>Servicio</Label>
          <Select value={offeringFilter} onValueChange={(val) => {
            updateQueryParams({ offeringId: val })
          }}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Todos los servicios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los servicios</SelectItem>
              {offerings?.map((offering) => (
                <SelectItem key={offering.id} value={offering.id}>
                  {offering.code} - {offering.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button className='ml-auto' onClick={handleSearch} variant="info">Buscar</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Planes de Pago</CardTitle>
          <CardDescription>
            Contrataciones financiadas en cuotas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentPlans && paymentPlans.data && paymentPlans.data.length > 0 ? (
            <DataTable
              columns={columns}
              data={paymentPlans.data}
              pageCount={paymentPlans.totalPages || -1}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              totalRecords={paymentPlans.total}
              onRowClick={handleSelect}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="mx-auto h-12 w-12 mb-4" />
              <p>No se encontraron planes de pago</p>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
